import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { EyeIcon, EyeOffIcon, LockIcon } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
const SPECIAL_EMAIL = 'stefan.bira@cautlocuinta.ro';
export function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    login
  } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<{
    email?: string;
    password?: string;
  }>({});
  const [loading, setLoading] = useState(false);
  const from = (location.state as any)?.from?.pathname || '/cursuri';
  const validateForm = () => {
    const newErrors: {
      email?: string;
      password?: string;
    } = {};
    if (!email) {
      newErrors.email = 'Email-ul este obligatoriu';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Email-ul nu este valid';
    }
    if (!password) {
      newErrors.password = 'Parola este obligatorie';
    } else if (password.length < 8) {
      newErrors.password = 'Parola trebuie să aibă minim 8 caractere';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    try {
      await login(email, password);
      // After successful login, always redirect to dashboard
      // Onboarding should only be triggered after completing Course 1, not on login
      navigate('/dashboard', {
        replace: true
      });
    } catch (error) {
      setErrors({
        password: 'Email sau parolă incorectă'
      });
    } finally {
      setLoading(false);
    }
  };
  return <div className="min-h-screen bg-[#F9FAFB] flex" data-id="element-7234">
      {/* Left side - Illustration & Copy */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-[#132C53] to-[#357BEB] text-white p-12 items-center justify-center relative overflow-hidden" data-id="element-7235">
        <div className="absolute inset-0 opacity-10" data-id="element-7236">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] bg-repeat" data-id="element-7237"></div>
        </div>
        <motion.div initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        duration: 0.6
      }} className="relative z-10 max-w-lg" data-id="element-7238">
          <div className="w-20 h-20 bg-white bg-opacity-20 rounded-2xl flex items-center justify-center mb-8" data-id="element-7239">
            <LockIcon className="w-10 h-10 text-white" data-id="element-7240" />
          </div>
          <h1 className="text-5xl font-bold mb-6" data-id="element-7241">
            Platforma agenților profesioniști
          </h1>
          <p className="text-xl text-gray-200 mb-8" data-id="element-7242">
            Accesează ecosistemul complet Cautlocuință.ro: cursuri, instrumente,
            suport 7/7 și comunitate.
          </p>
          <div className="space-y-4" data-id="element-7243">
            <div className="flex items-center space-x-3" data-id="element-7244">
              <div className="w-8 h-8 bg-[#21A366] rounded-full flex items-center justify-center" data-id="element-7245">
                <span className="text-white font-bold" data-id="element-7246">✓</span>
              </div>
              <span className="text-lg" data-id="element-7247">Training complet și actualizat</span>
            </div>
            <div className="flex items-center space-x-3" data-id="element-7248">
              <div className="w-8 h-8 bg-[#21A366] rounded-full flex items-center justify-center" data-id="element-7249">
                <span className="text-white font-bold" data-id="element-7250">✓</span>
              </div>
              <span className="text-lg" data-id="element-7251">Suport individual 7 din 7</span>
            </div>
            <div className="flex items-center space-x-3" data-id="element-7252">
              <div className="w-8 h-8 bg-[#21A366] rounded-full flex items-center justify-center" data-id="element-7253">
                <span className="text-white font-bold" data-id="element-7254">✓</span>
              </div>
              <span className="text-lg" data-id="element-7255">Comisionare progresivă</span>
            </div>
          </div>
        </motion.div>
      </div>
      {/* Right side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12" data-id="element-7256">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} className="w-full max-w-md" data-id="element-7257">
          <div className="bg-white rounded-2xl shadow-lg p-8" data-id="element-7258">
            <h2 className="text-3xl font-bold text-[#132C53] mb-2" data-id="element-7259">
              Autentificare în platformă
            </h2>
            <p className="text-gray-600 mb-8" data-id="element-7260">
              Conectează-te pentru a accesa cursurile și instrumentele tale
            </p>
            <form onSubmit={handleSubmit} className="space-y-6" data-id="element-7261">
              <div data-id="element-7262">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-7263">
                  Email
                </label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} className={`w-full px-4 py-3 border ${errors.email ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-[#357BEB] focus:border-transparent`} placeholder="exemplu@email.com" data-id="element-7264" />
                {errors.email && <p className="mt-1 text-sm text-red-600" data-id="element-7265">{errors.email}</p>}
              </div>
              <div data-id="element-7266">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-7267">
                  Parolă
                </label>
                <div className="relative" data-id="element-7268">
                  <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} className={`w-full px-4 py-3 border ${errors.password ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-[#357BEB] focus:border-transparent pr-12`} placeholder="••••••••" data-id="element-7269" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700" data-id="element-7270">
                    {showPassword ? <EyeOffIcon className="w-5 h-5" data-id="element-7271" /> : <EyeIcon className="w-5 h-5" data-id="element-7272" />}
                  </button>
                </div>
                {errors.password && <p className="mt-1 text-sm text-red-600" data-id="element-7273">{errors.password}</p>}
              </div>
              <div className="flex items-center justify-between" data-id="element-7274">
                <label className="flex items-center" data-id="element-7275">
                  <input type="checkbox" className="w-4 h-4 text-[#357BEB] border-gray-300 rounded focus:ring-[#357BEB]" data-id="element-7276" />
                  <span className="ml-2 text-sm text-gray-600" data-id="element-7277">
                    Ține-mă minte
                  </span>
                </label>
                <Link to="/auth/forgot-password" className="text-sm text-[#357BEB] hover:underline" data-id="element-7278">
                  Ai uitat parola?
                </Link>
              </div>
              <button type="submit" disabled={loading} className="w-full bg-[#357BEB] text-white py-3 rounded-lg font-semibold hover:bg-[#2868d8] transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-7279">
                {loading ? 'Se conectează...' : 'Conectează-te'}
              </button>
            </form>
            <div className="mt-6" data-id="element-7280">
              <div className="relative" data-id="element-7281">
                <div className="absolute inset-0 flex items-center" data-id="element-7282">
                  <div className="w-full border-t border-gray-300" data-id="element-7283"></div>
                </div>
                <div className="relative flex justify-center text-sm" data-id="element-7284">
                  <span className="px-2 bg-white text-gray-500" data-id="element-7285">sau</span>
                </div>
              </div>
              <Link to="/auth/register" className="mt-6 w-full flex items-center justify-center border-2 border-gray-300 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors" data-id="element-7286">
                Creează cont
              </Link>
            </div>
          </div>
          <p className="mt-6 text-center text-sm text-gray-600" data-id="element-7287">
            Întâmpini probleme?{' '}
            <Link to="/support" className="text-[#357BEB] hover:underline" data-id="element-7288">
              Contactează suportul
            </Link>
          </p>
        </motion.div>
      </div>
    </div>;
}